import { styled } from '@lichtblick/styled';
import { BorderRadius, Colors, Spacing } from '@lichtblick/theme';
import { ChipSizes, ChipVariants, ApprovalState, AppointmentStatus, WorkOrderStatus } from '@lichtblick/types';

export const RootContainer = styled.div`
  align-items: center;
  display: flex;
`;

const PaddedContent = styled.div<{ $size: ChipSizes }>`
  padding-bottom: ${({ $size }) => ($size === 'large' ? Spacing.Xxs : ' 0.3125rem')};
  padding-top: ${({ $size }) => ($size === 'large' ? Spacing.Xxs : ' 0.3125rem')};
`;

export const TextContainer = styled(PaddedContent)<{ $isDescriptive: boolean; $shouldShowIcon: boolean }>`
  display: flex;
  gap: ${({ $size }) => ($size === 'large' ? Spacing.Xxs : '0.38rem')};

  padding-left: ${({ $size }) => ($size === 'large' ? '0.75rem' : Spacing.Xxs)};
  padding-right: ${({ $isDescriptive, $shouldShowIcon, $size }) => {
    if ($isDescriptive) {
      if ($shouldShowIcon) {
        return $size === 'large' ? '0.25rem' : Spacing.Xxs;
      }

      return $size === 'large' ? '0.75rem' : Spacing.Xxs;
    } else {
      return $size === 'large' ? '0.75rem' : Spacing.Xxs;
    }
  }};
`;

export const CloseContainer = styled.div<{ $size: ChipSizes }>`
  align-items: center;
  display: flex;
  padding-left: ${({ $size }) => ($size === 'large' ? '0.25rem' : '0.1875rem')};
  padding-right: ${({ $size }) => ($size === 'large' ? '0.75rem' : '0.5rem')};
`;

export const BaseChip = styled.div<{ $isHighlighted?: boolean; $size: ChipSizes; $variant: ChipVariants }>`
  align-items: center;
  border-radius: ${({ $size }) => ($size === 'large' ? BorderRadius.Xs : BorderRadius.Xxs)};
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  height: ${({ $size }) => ($size === 'large' ? '2.5rem' : '1.5rem')};
  justify-content: center;
  line-height: 150%;
  ${({ $isHighlighted }) =>
    $isHighlighted &&
    `
      outline: 2px solid ${Colors.blue[100]};
    `}

  svg {
    width: ${({ $size }) => ($size === 'large' ? '1.5rem' : '1rem')};
  }

  > span {
    align-items: center;
    display: inline-flex;
    height: 100%;
    letter-spacing: 0.0313rem;
  }
  ${({ $variant }) => {
    switch ($variant) {
      case ApprovalState.ServerApproved:
      case ApprovalState.ClientApproved:
      case AppointmentStatus.Done:
      case WorkOrderStatus.Montage:
      case WorkOrderStatus.ConstructionFinished:
      case WorkOrderStatus.TechnicalCommisioning:
        return Green;
      case ApprovalState.Rejected:
      case AppointmentStatus.Aborted:
        return Red;
      case ApprovalState.Pending:
      case ApprovalState.Resubmitted:
      case ApprovalState.Undoed:
      case AppointmentStatus.Paused:
      case WorkOrderStatus.VotDocumentation:
        return Orange;
      case AppointmentStatus.Scheduled:
      case AppointmentStatus.Started:
        return Blue;
      case WorkOrderStatus.VotCoordination:
        return BlueDarker;
      case WorkOrderStatus.SettingElectricityMeter:
        return Violet;
      case AppointmentStatus.Open:
      case WorkOrderStatus.Finished:
        return Gray;
      case 'descriptive-lightblue':
        return DescriptiveLightblue;
      case 'descriptive-white':
        return DescriptiveWhite;
      case 'descriptive-grey':
        return DescriptiveGrey;
      case 'descriptive-turquoise':
      case WorkOrderStatus.Commisioning:
        return DescriptiveTurquoise;
      default:
        return DescriptiveLightblue;
    }
  }};
`;

export const CounterContainer = styled(BaseChip)`
  margin-left: ${({ $size }) => ($size === 'large' ? Spacing.Xxs : Spacing['3Xs'])};
  width: ${({ $size }) => ($size === 'large' ? '2.5rem' : '1.5rem')};
`;

const Green = `
  background-color: ${Colors.green[10]};
  color: ${Colors.green[100]};

  > a {
    color: ${Colors.green[100]};
  }
`;

const Red = `
  background-color: ${Colors.red[10]};
  color: ${Colors.red[100]};

  > a {
    color: ${Colors.red[100]};
  }
`;

const Orange = `
  background-color: ${Colors.orange[10]};
  color: ${Colors.orange[100]};

  > a {
    color: ${Colors.orange[100]};
  }
`;

const Gray = `
  background-color: ${Colors.gray[10]};
  color: ${Colors.gray[100]};

  > a {
    color: ${Colors.gray[100]};
  }
`;

const Blue = `
  background-color: ${Colors.blue[5]};
  color: ${Colors.darkBlue};

  > a {
    color: ${Colors.darkBlue};
  }
`;

const BlueDarker = `
  background-color: ${Colors.blue['20']};
  color: ${Colors.darkBlue};

`;

const Violet = `
  background-color: ${Colors.violet['20']};
  color: ${Colors.violet['100']};

`;

const Descriptive = `
  color: ${Colors.black};

  > a {
    color: ${Colors.black};
  }

`;

const DescriptiveLightblue = `
  ${Descriptive}

  background-color: ${Colors.blue[3]};

  &:hover {
    background-color: ${Colors.blue[20]};
  }
`;

const DescriptiveWhite = `
${Descriptive}
  background-color: ${Colors.white};

  &:hover {
    background-color: ${Colors.blue[5]};
  }
`;

const DescriptiveGrey = `
${Descriptive}
  background-color: ${Colors.gray[20]};

  &:hover {
    background-color: ${Colors.gray[50]};
  }
`;

const DescriptiveTurquoise = `
${Descriptive}
  background-color: ${Colors.turquoise[10]};

  &:hover {
    background-color: ${Colors.turquoise[10]};
  }
`;
